import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  create_category,
  delete_category,
  fetch_categories,
  update_category,
  upload_category_icon,
} from './api';
import type { CreateCategoryInput, UpdateCategoryInput } from './types';

const KEY = 'admin-product-categories';

/**
 * The category list, shared by every screen that offers one.
 *
 * Long `staleTime` on purpose: this is fetched by the approvals queue, the catalogue, the
 * catalogue editor and the fees screen, and it changes a few times a year. Refetching it
 * on every navigation would be four requests for a list that has not moved.
 */
export function use_product_categories() {
  return useQuery({
    queryKey: [KEY],
    queryFn: fetch_categories,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Adding or renaming a category changes what the catalogue, the queue and the fees screen
 * are allowed to show, so all of them are dropped rather than only this list.
 */
function invalidate_everything(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [KEY] });
  qc.invalidateQueries({ queryKey: ['admin-catalog-products'] });
  qc.invalidateQueries({ queryKey: ['admin-catalog-counts'] });
  qc.invalidateQueries({ queryKey: ['admin-commission-coverage'] });
  qc.invalidateQueries({ queryKey: ['admin-product-queue'] });
}

export function use_upload_category_icon() {
  return useMutation({ mutationFn: (file: File) => upload_category_icon(file) });
}

export function use_create_category() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryInput) => create_category(input),
    onSuccess: () => invalidate_everything(qc),
  });
}

export function use_update_category() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) =>
      update_category(id, input),
    onSuccess: () => invalidate_everything(qc),
  });
}

export function use_delete_category() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => delete_category(id),
    onSuccess: () => invalidate_everything(qc),
  });
}
